/**
 * This component implements a step UI for creating a subscription.
 * It replaces CustomerAppCreate.
 * 
 * If this is called with an accountId parameter, this will have 2 configuration steps
 * (select app, provision bindcard).  Otherwise, the provisioner needs to select the account
 * for which a subscription is being provided, so there will be 3 configuration steps
 * (select customer, select app, provision bindcard).
 * 
 * User information for the bindcard depends on both the bindcard user and provisioning user.
 * In the mount lifecycle, if the using account is passed in, we'll hydrate the bcUser information.
 * If the user and provisioner are the same, we can hydrate from the current account data using
 * abEnvironment.getAccountContact();
 * 
 * If the user and provisioner are not the same, we'll fetch account information from the db
 * for hydration.
 * 
 * The provision bindcard step may eventually allow choice of customer or provisioner user
 * information, but for now set to user (main contact on using account record).
 * 
 */
import React from 'react';
import { Redirect } from 'react-router-dom';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';

import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, Stepper, Step, StepLabel, Typography} from '@mui/material';

import AbEnvironment from '../auth/AbEnvironment';

import SubscriptionAppSelect from './SubscriptionAppSelect';

import CustomerBindCardProvision from './CustomerBindCardProvision';

import AccountSuspendMsg from '../ui/AccountSuspendMsg';
import SaveButton from '../ui/SaveButton';
import PageTitle from '../ui/PageTitle';
import AccountFetch from '../account/AccountFetch';
import AppParameterFetch from './AppParameterFetch';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';

const abEnvironment = new AbEnvironment();

const SubscriptionCreateForSelfQuery = graphql`
query SubscriptionCreateForSelfQuery($accountId: UUID!) {
  allProjectTs(condition: {customerAccountId: $accountId, resellerAccountId: $accountId}) {
      totalCount
      nodes {
        projectId
        projectName
        projectStart
        projectStatus
      }
    }
  
  }
`;


const styles = theme => ({
  root: {
    width: '100%',
  },
  paper1: {
    padding: theme.spacing(3),
    textAlign: 'left',
    color: theme.palette.text.secondary,
    maxWidth: 980,
    marginTop: 16
  },
  button: {
    marginRight: 16,
    marginTop: 16
  },
  instructions: {
    fontSize: theme.fontSize+1,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  instructions2: {
    fontSize: theme.fontSize+1,
    margin: theme.spacing(1),
  },
  instructionsDiv: {
    background: '#eee',
  },
  stepper: {
    marginTop: 16
  },
  finalPanelRight: {
    backgroundColor: "#EEEEEE",
  },
  smallButton: {
    width: 40,
    maxWidth: 40, 
    minWidth: 40, 

  }
});

class SubscriptionCreateForSelf extends React.Component {
  static propTypes = {
    classes: PropTypes.object,
  };

  constructor(props) {
    super(props);

    let activeStep = 0;
    let custStep = -1;
    let appStep = 0;
    let bindcardStep = 1;
    let showBack = true;
    let backName = 'Subscriptions';

    const params = new URLSearchParams(this.props.location.search);

    let usingAccountId = params.get('accountId');
    if (usingAccountId != null)
    {
      activeStep = 1;

      let from = params.get('f');
      if (from != null && from === 'db')
      {
        backName = 'Dashboard';
      }
    }
    else
    {
        let from = params.get('f');
        if (from != null && from === 'db')
        {
          backName = 'Dashboard';
        } else {
          showBack = false;
        }
    }

    this.mode = {
      customerStep: custStep,
      appStep: appStep,
      bindcardStep: bindcardStep,
      showBack: showBack,
      backName: backName,
    }

    this.state = {
      saveStatus: null,
      saveMessage: null,
      saveSnackbarOpen: false,
      saveSnackbarDuration: null,
      activeStep: activeStep,
      skipped: new Set(),
      searchP: '',
      appName: null,
      appId: null,
      createdCustomerAppId: null,
      cardAttached: null,
      linkURL: null,
      cApp: {
        appId: null,
        accountId: null,
        appStatus: null,
        parentAppId: null,
        oemAppId: null,
        appName: null,
        appSku: null,
        appUrl: null,
        signupUrl: null,
        appLogo: null,
        description: null,
        appType: null,
        atId: null,
        appSkuPrice: null,
        appMarkupAbsolute: null,
        appMarkupPercentage: null,
        marqetaMid: null,
        marqetaMname: null,
        maxCharge: null,
        paymentOrder: null,
        passThroughFees: null,
        showSubscription: null,
        notes: null, // Customer App only
        appBindAddress: null,
      },
      bcUser: {
        userId:         null,
        usingAccountId: null,
        accountName:    null,
        description:    null,
        lastName:       null,
        firstName:      null,
        email:          null,
        phone:          null,
        address1:       null,
        address2:       null,
        city:           null,
        state:          null,
        country:        null,
        postalCode:     null,
        marqetaUid:     null,
        mCardToken:     null,
      },
      pbcUser: {
        userId:         null,
        usingAccountId: null,
        accountName:    null,
        description:    null,
        lastName:       null,
        firstName:      null,
        email:          null,
        phone:          null,
        address1:       null,
        address2:       null,
        city:           null,
        state:          null,
        country:        null,
        postalCode:     null,
        marqetaUid:     null,
        mCardToken:     null,
      },
    };
  }

  componentDidMount() {
    if (abEnvironment.isSegmentActive()) { window.analytics.page(); }

    let expensingActive = false;
    let expensing = abEnvironment.getFlagValue("expensing");
    if (expensing != null && expensing.flagValue === 1) {
      expensingActive = true;
    }

    //
    // Hydrate the bcUser here.
    // - if an account_id has been passed, this will be the using account for the
    //   subscription.  If the using account is not the same as the current user
    //   account (provisioner), lookup address information from the db.  Otherwise,
    //   set from the account info in the context
    //

    // Start with the provisioning account
    let thePbcUser = {
      userId:         abEnvironment.getUserAttribute('u_id'),
      usingAccountId: abEnvironment.getUserAttribute('a_id'),
      accountName:    abEnvironment.getUserAttribute('a_accountName'),
      description:    abEnvironment.getUserAttribute('a_description'),
      lastName:       abEnvironment.getUserAttribute('a_lastName'),
      firstName:      abEnvironment.getUserAttribute('a_firstName'),
      email:          abEnvironment.getUserAttribute('a_email'),
      phone:          abEnvironment.getUserAttribute('a_phone'),
      address1:       abEnvironment.getUserAttribute('a_address1'),
      address2:       (abEnvironment.getUserAttribute('a_address2') ? abEnvironment.getUserAttribute('a_address2') : ''),
      city:           abEnvironment.getUserAttribute('a_city'),
      state:          abEnvironment.getUserAttribute('a_state'),
      country:        abEnvironment.getUserAttribute('a_country'),
      postalCode:     abEnvironment.getUserAttribute('a_postalCode'),
      marqetaUid:     null,
      mCardToken:     null,
    };
     
    const params = new URLSearchParams(this.props.location.search);
    let usingAccountId = params.get('accountId');
    if (usingAccountId != null && usingAccountId !== thePbcUser.usingAccountId) {
      // Lookup the using account id
      AccountFetch.fetch(usingAccountId).toPromise()
      .then(af => {
        let theBcUser = {
          userId:         abEnvironment.getUserAttribute('u_id'), // Still use provisioner user id
          usingAccountId: usingAccountId,
          accountName:    af.accountTByAccountId.accountName,
          description:    af.accountTByAccountId.description,
          lastName:       af.accountTByAccountId.contactLastName,
          firstName:      af.accountTByAccountId.contactFirstName,
          email:          af.accountTByAccountId.contactEmail,
          phone:          af.accountTByAccountId.contactPhone,
          address1:       af.accountTByAccountId.address1,
          address2:       af.accountTByAccountId.address2,
          city:           af.accountTByAccountId.city,
          state:          af.accountTByAccountId.state,
          country:        af.accountTByAccountId.country,
          postalCode:     af.accountTByAccountId.postalCode,
          marqetaUid:     null,
          mCardToken:     null,
        };
        this.setState({bcUser: theBcUser, pbcUser: thePbcUser});
      })
      .catch(err => {
        this.setState({bcUser: thePbcUser, pbcUser: thePbcUser});
      });
    } else {
      this.setState({bcUser: thePbcUser, pbcUser: thePbcUser});
    }

    let appId = params.get('appId');
    if (appId != null) {
      // Lookup the using account id
      AppParameterFetch.fetch(appId).toPromise()
      .then(apf => {

        //do not allow an appname > 64!
        let appName = apf.appTByAppId.appName;
        if (appName.length > 64) {
          appName = appName.substring(0, 64)
        }

        // Setup the new capp...
        let paymentOrder = apf.appTByAppId.paymentOrder;
        if (paymentOrder == null) {
          paymentOrder = 5;
        } else {
          if (expensingActive !== true && (paymentOrder === 0 || paymentOrder === 1 || paymentOrder === 2)) {
            paymentOrder = 5;
          }
        }

        //set the default pass through fees
        let passThroughFees = apf.appTByAppId.passThroughFees;
        if (passThroughFees == null) {
          passThroughFees = 1;
        }

        let notesval = apf.appTByAppId.notes;
        if (notesval == null) {
          notesval = '';
        }

        let theAppSelected = {
          appId: apf.appTByAppId.appId,
          accountId: apf.appTByAppId.accountId,
          appStatus: apf.appTByAppId.appStatus,
          parentAppId: apf.appTByAppId.parentAppId,
          oemAppId: apf.appTByAppId.oemAppId,
          appName: appName,
          appSku: apf.appTByAppId.appSku,
          appUrl: apf.appTByAppId.appUrl,
          signupUrl: apf.appTByAppId.signupUrl,
          appLogo: apf.appTByAppId.appLogo,
          description: apf.appTByAppId.description,
          appType: apf.appTByAppId.appType,
          atId: apf.appTByAppId.atId,
          appSkuPrice: apf.appTByAppId.appSkuPrice,
          appMarkupAbsolute: apf.appTByAppId.appMarkupAbsolute,
          appMarkupPercentage: apf.appTByAppId.appMarkupPercentage,
          marqetaMid: apf.appTByAppId.marqetaMid,
          marqetaMname: apf.appTByAppId.marqetaMname,
          maxCharge: apf.appTByAppId.maxCharge,
          paymentOrder: paymentOrder,
          passThroughFees: passThroughFees,
          showSubscription: 1,
          notes: notesval,
        }
        this.setState({appId: appId, cApp: theAppSelected});
     })
      .catch(err => {
        //do nothing some sort of problem fetching app passed in.  Ignore.
      });
    } 

    const { callPartnerApi } = this.props.auth;


    let payload = {
      operation: "getAppBindAddress",
      params: {}
    }
    
    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    ).then(resp => {
      return resp.json();
    })
    .then(response => {
      //console.log(response);
      this.setState({appBindAddress: response});
    });

  }

  getSteps = () => {
    return ['Select App', 'Confirm'];
    /*if (this.mode.customerStep === 0) {
      let isFor = 'Who is it for?';
      let whatApp = 'What app?';

      if (this.state.activeStep > 0) {
        isFor = "For " + this.state.bcUser.accountName;
      }
      if (this.state.activeStep > 1) {
        whatApp = this.state.cApp.appName;
      }
      return [isFor, whatApp, 'Confirm'];
      // return ['Who is it for?', 'What app?', 'Confirm'];
    } else {
      return ['Select App', 'Confirm'];
    }*/
  }
  
  /*
   * Get the component to render the current step.  Steps are different depending on whether or
   * not the customer needs to be chosen.  Using if instead of switch as case numbers are variable
   * depending on scenario.
   */
  getStepContent = step => {
    if (step === this.mode.appStep) {
        return <SubscriptionAppSelect bcUser={this.state.bcUser}
                                      cApp={this.state.cApp} 
                                      usingAccountId={this.state.bcUser.usingAccountId}
                                      getSearchPState={this.getSearchPState} 
                                      setAppCreateState={this.setAppCreateState} 
                                      setAppCreateStateApp={this.setAppCreateStateApp}/>;
    } else if (step === this.mode.bindcardStep) {
        return <CustomerBindCardProvision bcUser={this.state.bcUser}
                                          pbcUser={this.state.pbcUser}
                                          cApp={this.state.cApp}
                                          setAppCreateStateBcUser={this.setAppCreateStateBcUser}
                                          appBindAddress={this.state.appBindAddress}/>;
    }

    // Fallthrough means unknown step
    return 'Unknown step';
  }
    
  isStepOptional = step => {
    return false;
  }

  isStepSkipped(step) {
    return this.state.skipped.has(step);
  }

  setAppCreateState = (avalue) => {
    this.setState(avalue);
  }

  getSearchPState = () => {
    return this.state.searchP;
  }

  setAppCreateStateApp = (akey, avalue) => {
    let tApp = this.state.cApp;
    tApp[akey] = avalue;
    this.setState({cApp: tApp});
  }

  setAppCreateStateBcUser = (akey, avalue) => {
    let tUser = this.state.bcUser;
    tUser[akey] = avalue;
    this.setState({bcUser: tUser});
  }
 
  /*
   * Special handling on the step to provision a bindcard
   */
  handleNext = (props) => {

    let projectId = null;
    //see if this account has a project for themselves.
    if (props && props.allProjectTs.totalCount > 0) {
      projectId = props.allProjectTs.nodes[0].projectId;
    }

    const { callPartnerApi } = this.props.auth;
    const { activeStep } = this.state;
    let { skipped } = this.state;
    if (this.isStepSkipped(activeStep)) {
      skipped = new Set(skipped.values());
      skipped.delete(activeStep);
    }

    /*
      If active step is bindcard provisioning, need to:
       - link bc user to marqeta user if not already done
       - create a bind card
       - attach to  
    */
    if (activeStep === this.mode.bindcardStep)
    {
      if (abEnvironment.isSegmentActive()) {  window.analytics.track('Subscription create (saving subscription)'); }

      //determine proper information to finally put on virtual card.  Start with provisioner's address as default
      let billingFirstName  = this.state.pbcUser.firstName;
      let billingLastName   = this.state.pbcUser.lastName;
      let billingEmail      = this.state.pbcUser.email;
      let billingPhone      = this.state.pbcUser.phone;
      let billingAddress1   = this.state.pbcUser.address1;
      let billingAddress2   = this.state.pbcUser.address2;
      let billingCity       = this.state.pbcUser.city;
      let billingState      = this.state.pbcUser.state;
      let billingCountry    = this.state.pbcUser.country;
      let billingPostalCode = this.state.pbcUser.postalCode;
      
      let usingAccount = this.state.bcUser.usingAccountId;
      let cApp = this.state.cApp;
      let thePaymentOrder = cApp.paymentOrder;

      if (usingAccount === abEnvironment.getUserAttribute('a_id')) {
        // Check the paymenOrder.  If 4, and purchasing for self, make it 5.
        if (thePaymentOrder === 4) {
          thePaymentOrder = 5;
        }
      } else {
        // If not bill to me (ie a payment order = 2), use the customer info
        if (this.state.cApp.paymentOrder !== 2) {
          billingFirstName = this.state.bcUser.firstName;
          billingLastName = this.state.bcUser.lastName;
          billingEmail = this.state.bcUser.email;
          billingPhone = this.state.bcUser.phone;

          // If the customer address exists now, show it.
          // Otherwise, keep the reseller address for now.
          // it may be changed when customer adds funding source.
          if (this.state.bcUser.address1   != null && this.state.bcUser.address1   !== '' &&
              this.state.bcUser.city       != null && this.state.bcUser.city       !== '' &&
              this.state.bcUser.country    != null && this.state.bcUser.country    !== '' &&
              this.state.bcUser.postalCode != null && this.state.bcUser.postalCode !== '' ) {
            billingAddress1   = this.state.bcUser.address1;
            billingAddress2   = this.state.bcUser.address2;
            billingCity       = this.state.bcUser.city;
            billingState      = this.state.bcUser.state;
            billingCountry    = this.state.bcUser.country;
            billingPostalCode = this.state.bcUser.postalCode;
          }      
        }
      }
  
      this.setState({ saveStatus: "saving"});

      let provisioningAccountId = abEnvironment.getUserAttribute('a_id');
      let provisioningAccountName = abEnvironment.getUserAttribute('a_accountName');
  
      // Need bind card user state data available within closure.
      let bcUser = this.state.bcUser;
      let usingAccountId = bcUser.usingAccountId;
      let usingAccountName = bcUser.accountName;
  
      let m_user_token = null;
      let m_card_token = null;
      let cardAttached = null;
      let m_resp = null;
      let returnedAppId = null;
      let returnedCustomerAppId = null;
   
        // Initiate promise chain.
      Promise.resolve()  
      .then(() => {
        // Call the server side provisioning
        let aPjwt = abEnvironment.getPjwt();

        let payload;
        if (thePaymentOrder === 0 || thePaymentOrder === 1 || thePaymentOrder === 2) {
          payload = {
            operation: "createSubscriptionForProject",
            params: {
              pJwt:                aPjwt,
              accountId:           usingAccountId,
              accountName:         usingAccountName,
              pAccountId:          provisioningAccountId,
              pAccountName:        provisioningAccountName,
              appId:               cApp.appId,
              appAccountId:        cApp.accountId,
              appStatus:           cApp.appStatus,
              appName:             cApp.appName,
              appSku:              cApp.appSku,
              appUrl:              cApp.appUrl,
              signupUrl:           cApp.signupUrl,
              appLogo:             cApp.appLogo,
              description:         cApp.description,
              notes:               cApp.notes,
              appType:             cApp.appType,
              parentAppId:         cApp.parentAppId,
              oemAppId:            cApp.oemAppId,
              atId:                cApp.atId,
              appSkuPrice:         cApp.appSkuPrice,
              appMarkupAbsolute:   cApp.appMarkupAbsolute,
              appMarkupPercentage: cApp.appMarkupPercentage,
              wholesaleDiscountPercentage: cApp.wholesaleDiscountPercentage,
              jitFloat:            0,
              marqetaMid:          cApp.marqetaMid,
              marqetaMname:        cApp.marqetaMname,
              maxCharge:           cApp.maxCharge,
              paymentOrder:        thePaymentOrder,
              passFees:            cApp.passThroughFees,
              showSubscription:    cApp.showSubscription,
              bcUserId:            bcUser.userId,
              bcLastName:          billingLastName,
              bcFirstName:         billingFirstName,
              bcEmail:             billingEmail,
              bcPhone:             billingPhone,
              bcAddress1:          billingAddress1,
              bcAddress2:          billingAddress2,
              bcCity:              billingCity,
              bcState:             billingState,
              bcCountry:           billingCountry,
              bcPostalCode:        billingPostalCode
            }
          };
        } else {
          payload = {
            operation: "createAuthOnlySubscriptionForProject",
            params: {
              pJwt:                aPjwt,
              accountId:           usingAccountId,
              accountName:         usingAccountName,
              pAccountId:          provisioningAccountId,
              pAccountName:        provisioningAccountName,
              appId:               cApp.appId,
              appAccountId:        cApp.accountId,
              appStatus:           cApp.appStatus,
              appName:             cApp.appName,
              appSku:              cApp.appSku,
              appUrl:              cApp.appUrl,
              signupUrl:           cApp.signupUrl,
              appLogo:             cApp.appLogo,
              description:         cApp.description,
              notes:               cApp.notes,
              appType:             cApp.appType,
              parentAppId:         cApp.parentAppId,
              oemAppId:            cApp.oemAppId,
              atId:                cApp.atId,
              appSkuPrice:         cApp.appSkuPrice,
              appMarkupAbsolute:   cApp.appMarkupAbsolute,
              appMarkupPercentage: cApp.appMarkupPercentage,
              wholesaleDiscountPercentage: cApp.wholesaleDiscountPercentage,
              jitFloat:            0,
              marqetaMid:          cApp.marqetaMid,
              marqetaMname:        cApp.marqetaMname,
              maxCharge:           cApp.maxCharge,
              paymentOrder:        thePaymentOrder,
              passFees:            cApp.passThroughFees,
              showSubscription:    cApp.showSubscription,
              bcUserId:            bcUser.userId,
              bcLastName:          billingLastName,
              bcFirstName:         billingFirstName,
              bcEmail:             billingEmail,
              bcPhone:             billingPhone,
              bcAddress1:          billingAddress1,
              bcAddress2:          billingAddress2,
              bcCity:              billingCity,
              bcState:             billingState,
              bcCountry:           billingCountry,
              bcPostalCode:        billingPostalCode,
              soEntered:           0,
              useEmail:            (cApp.useEmail == null) ? '' : cApp.useEmail,
              subPassword:         null,
            }
          };
        }

        return callPartnerApi('appbind/generic',
                              'POST',
                                payload);
      })
      .then((response) => {
        return response.json();
      })
      .then((jresp) => {
        m_resp = jresp; // For trace purposes

        // - the return from the new flow is on a data attribute
        //set appId if a new one was created
        returnedAppId = jresp.data.appId;
        returnedCustomerAppId = jresp.data.customerAppId;
        cardAttached  = jresp.data.cardAttached;

        m_user_token = jresp.data.m_user_token;
        m_card_token = jresp.data.m_card_token;

        let subsCount = parseInt(abEnvironment.getUserAttribute("subs_count"), 10) + 1;
        abEnvironment.setUserAttribute("subs_count", subsCount);

        if ((m_user_token != null && m_card_token != null) || (m_user_token === null && m_card_token === null && cardAttached === false))
        {
          if (abEnvironment.isSegmentActive()) {  window.analytics.track('Subscription create (subscription saved)'); } 
          this.setState({
            appId: returnedAppId,
            createdCustomerAppId: returnedCustomerAppId,
            cardAttached: cardAttached,
            activeStep: activeStep + 1,
            saveStatus: 'success',
            saveMessage: 'Subscription created!',
            skipped,
            bcUser: {
              userId:         bcUser.userId,
              usingAccountId: bcUser.usingAccountId,
              accountName:    bcUser.accountName,
              description:    bcUser.description,
              lastName:       billingLastName,
              firstName:      billingFirstName,
              phone:          billingPhone,
              address1:       billingAddress1,
              address2:       billingAddress2,
              city:           billingCity,
              state:          billingState,
              email:          billingEmail,
              country:        billingCountry,
              postalCode:     billingPostalCode,
              marqetaUid:     m_user_token,
              mCardToken:     m_card_token,
            },
          });
        }
        else
        {
          this.setState({
            activeStep: activeStep + 1,
            skipped,
            saveStatus: 'error',
            saveMessage: 'Unable to create the subscription: ' + m_resp
          });
        }
      })
      .catch((error) => {
        this.setState({
          activeStep: activeStep + 1,
          skipped,
          saveStatus: 'error',
          saveMessage: 'There was a problem creating the subscription.  Please contact support.'
        });
      });

    }  // If Step is bindcard provision step  
    else
    {        
      // Update the active step.
      this.setState({
        activeStep: activeStep + 1,
        skipped,
      });
    }
  };

  handleBack = () => {
    const { activeStep } = this.state;
    this.setState({
      activeStep: activeStep - 1,
    });
  };

  handleSkip = () => {
    const { activeStep } = this.state;
    if (!this.isStepOptional(activeStep)) {
      // You probably want to guard against something like this,
      // it should never occur unless someone's actively trying to break something.
      throw new Error("You can't skip a step that isn't optional.");
    }
    const skipped = new Set(this.state.skipped.values());
    skipped.add(activeStep);
    this.setState({
      activeStep: this.state.activeStep + 1,
      skipped,
    });
  };

  handleReset = () => {
    this.setState({
      activeStep: 0,
    });
  };

  handleReturnToCustomer = () => {
    this.goBack();
  }

  goBack() {
    this.props.history.goBack();
  }

  isBackDisabled() {
    if (this.state.activeStep === 0 || this.state.activeStep === 3)
      return true;

    if (this.state.activeStep === 1 && abEnvironment.getUserAttribute("cust_count") === '0')
      return true;

    return false;
  }

  isNextDisabled() {
    if (this.state.activeStep === this.mode.customerStep) {
      if (this.state.bcUser.usingAccountId == null)
        return true;
    } else if (this.state.activeStep === this.mode.appStep) {
      if (this.state.appId == null || this.state.cApp.appName === null || this.state.cApp.appName === "")
        return true;
    } else if (this.state.activeStep === this.mode.bindcardStep) {
      if (this.state.appId == null || this.state.cApp.appName === null || this.state.cApp.appName === "" ||
          this.state.bcUser.postalCode  == null || this.state.bcUser.postalCode)
        return true;
    }
    return false;
  }

  _onSnackbarClose() {
    this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
  }

  
  render() {
    const { isAuthenticated, isAuthenticatedReseller, isAuthenticatedCustomer, isTransferredCustomer, isAccountSuspended, needAssent, logError } = this.props.auth;
    const { classes } = this.props;
    const steps = this.getSteps();
    const { activeStep } = this.state;

    if (!isAuthenticated() || needAssent()) {
      return (
        <Redirect to='/home' />
      );
    } else if (isAuthenticatedReseller() || (isAuthenticatedCustomer() && isTransferredCustomer())) {
      let expensingActive = false;
      let expensing = abEnvironment.getFlagValue("expensing");
      if (expensing != null && expensing.flagValue === 1) {
        expensingActive = true;
      }          

      let accountId = abEnvironment.getUserAttribute("a_id");

      return (         
        <QueryRenderer
          environment={abEnvironment.getEnvironment()}
          query={SubscriptionCreateForSelfQuery}
          variables={{
            accountId: accountId,
            pAccountId: accountId,
          }}
          render={({ error, props }) => {
            if (error) {
              logError(error.message);
              return (
                <ServerError/>
              );
            } else if (props) {
              // At the end of flow on success, when redirecting to the subscription page, use
              // the same back button label as on the flow if applicable.
              let theRedirect = 'subscription?customerAppId=' + this.state.createdCustomerAppId;
              if (this.mode.showBack) {
                theRedirect = theRedirect + '&fp=' + this.mode.backName;
              } else {
                theRedirect = theRedirect + '&fp=Back';
              }
              theRedirect = theRedirect + '&from=create';

              if (isAccountSuspended()) {
                let accountType = abEnvironment.getUserAttribute("a_aType");
                let accountStatus = abEnvironment.getUserAttribute("a_aStatus");

                return (
                  <div className={classes.root}>
                    <PageTitle title="Buy subscription" subtitle="Purchase and manage a new subscription for yourself."/>
                    <AccountSuspendMsg accountType={accountType} accountStatus={accountStatus}/>
                  </div>
                );
              }

              if (!isAccountSuspended()) {
                let pTitle = "Add subscription";
                let pSubtitle = "Add a subscription for yourself.";

                if (expensingActive === true) {
                  pTitle = "Buy subscription";
                  pSubtitle = "Purchase and manage a new subscription.";
                }

                return (
                  <Grid container spacing={3} direction="row" justifyContent="flex-start">
                    <Grid item xs={12}>
                      {this.mode.showBack &&
                      <PageTitle title={pTitle} subtitle={pSubtitle} showBackButton={true} name={this.mode.backName} noMargin={true} history={this.props.history}/>}
                      {!this.mode.showBack &&
                      <PageTitle title={pTitle} subtitle={pSubtitle} noMargin={true} history={this.props.history}/>}
                    </Grid>
                    <Grid item xs={12}>
                      <div className={classes.root}>
                        <div className={classes.stepper}>
                          <Stepper activeStep={activeStep} style={{backgroundColor: 'inherit'}}>
                            {steps.map((label, index) => {
                              const props = {};
                              const labelProps = {};
                              if (this.isStepOptional(index)) {
                                labelProps.optional = <Typography variant="caption">Optional</Typography>;
                              }
                              if (this.isStepSkipped(index)) {
                                props.completed = false;
                              }
                              return (
                                <Step key={label} {...props}>
                                  <StepLabel {...labelProps}>{label}</StepLabel>
                                </Step>
                              );
                            })}
                          </Stepper>
                        </div>
                        <div>
                          {activeStep === steps.length ? (
                            <div>
                              {this.state.saveStatus === 'error' &&
                              <div className={classes.instructionsDiv}>
                                <Typography variant="body1" className={classes.instructions2}>
                                  {this.state.saveMessage}
                                </Typography>
                              </div>}
                              {this.state.saveStatus !== 'error' &&
                              <Redirect push to={theRedirect} />}
                            </div>
                          ) : (
                            <div>
                              {this.getStepContent(activeStep)}
                              <div style={{marginTop:'15px'}}>
                                <Button
                                  disabled={this.isBackDisabled()}
                                  color="primary" variant="outlined"
                                  onClick={this.handleBack}
                                  className={classes.button}
                                >
                                  Back
                                </Button>
                                {this.isStepOptional(activeStep) && (
                                  <Button
                                    color="primary"
                                    onClick={this.handleSkip}
                                    className={classes.button}
                                  >
                                    Skip
                                  </Button>
                                )}
                                {activeStep === steps.length - 1 ? 
                                  <SaveButton
                                    snackbarOpen={this.state.saveSnackbarOpen}
                                    autoHideDuration={this.state.saveSnackbarDuration}
                                    label="Confirm"
                                    status={this.state.saveStatus} 
                                    message={this.state.saveMessage}
                                    disabled={false}
                                    onSaveClick={ () => this.handleNext(props) }
                                    onSnackbarClose={() => { this._onSnackbarClose(); }}
                                  />                  
                                :
                                  <Button
                                    disabled={this.isNextDisabled()} 
                                    color="primary" variant="contained" onClick={() => this.handleNext(props)} className={classes.button}>
                                    Next
                                  </Button>
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                );
              }
            }
            return (
              <LoadingSpinner />
            );
          }}
        />
      );
    } else {
      return (
        <Redirect to='/home' />
      );
    }    
  }
}

export default withStyles(styles)(SubscriptionCreateForSelf);

